import { Injectable, OnDestroy, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { asyncScheduler, BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';

import { StateService } from './base-states/base-state';
import { AppUtils } from '../../shared/helpers/app.utils';
import { INotificationStateModel } from '../../shared/models/notification.model';
import { NotificationService } from '../http-services/notification.service';

const initialState: INotificationStateModel = {
  count: 0,
  notifications: [],
  selectedType: 10,
};

@Injectable({
  providedIn: 'root',
})
export class NotificationStateService extends StateService<INotificationStateModel> implements OnDestroy {
  isPanelOpen = signal(false);
  user$: Observable<INotificationStateModel> = this.select((state) => state);
  sidebarSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  sidebarState = this.sidebarSubject.asObservable();

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly appUtils: AppUtils,
    private readonly notificationService: NotificationService,
    private readonly snackBar: MatSnackBar
  ) {
    super(initialState);
    asyncScheduler.schedule(() => {
      if (this.appUtils.isUserAuthenticated()) {
        this.updateCountState();
      }
    }, 100);
  }

  updateCountState() {
    this.notificationService.unseenCount().subscribe({
      next: (response) => {
        this.updateNotificationState();
        this.setState({
          ...this.state,
          count: response.data,
        });
      }
    });
  }

  updateNotificationState(type?: number): void {
    const current = this.state;
    const usedType = type ?? current.selectedType;

    this.notificationService.getAll(usedType).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.setState({
          ...current,
          notifications: response,
          selectedType: usedType
        });
      },
      error: (err) => {
        this.appUtils.processErrorResponse(this.snackBar, err);
      }
    });
  }

  getState() {
    return this.select((state) => state);
  }

  togglePanel(): void {
    this.isPanelOpen.set(!this.isPanelOpen());
  }

  closePanel(): void {
    this.isPanelOpen.set(false);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
